import {
  type HygienaAuthElementApi,
  type HygienaAuthAttribute,
  type HygienaConfiguration,
  type UnifiedLoginStateUpdate,
  type HygienaEventMessage,
  type HygienaToken,
  type SessionAccount,
  type SessionToken,
  type HygienaAuthSlotLabel,
  type HygienaAuthResponse,
  registerHygienaAuthElement,
  HygienaAuthError,
  HygienaClient,
  HygienaAuthElement,
} from "@ckweb/foundation/hygiena-auth";

export type { HygienaEventMessage, HygienaToken, SessionAccount, SessionToken, HygienaAuthResponse };
export { HygienaAuthError };

interface HygienaLoginOptions {
  loginHint?: string;
  /**
   * if the user is coming from a signup/accept-invite form
   */
  password?: string;
  redirectUri?: string;
}

interface HygienaLogoutOptions {
  postLogoutRedirectUri?: string;
}

export interface HygienaInstanceConfig extends HygienaConfiguration {
  attributes?: Partial<Record<HygienaAuthAttribute, MaybeGetter<string>>>;
  labels?: Partial<Record<HygienaAuthSlotLabel, MaybeGetter<string>>>;
  elementContainer?: HygienaAuthElement | HTMLElement | string;
  redirectUri?: string;
  postLogoutRedirectUri?: string;
}

export class HygienaInstance extends HygienaClient {
  #boundComponent: HygienaAuthElementApi | undefined;
  #abortController: AbortController | undefined;
  #config: HygienaInstanceConfig;

  constructor(config: HygienaInstanceConfig) {
    super({
      storageType: "localStorage",
      ...config,
    });
    this.#config = config;
    this.addEventCallback((event) => {
      if (event.type === "auth-busy-state-change") {
        this.#boundComponent?.setAttribute("busy", event.detail?.busy ? "true" : "false");
      }
    });
  }

  get elementContainer(): HTMLElement | undefined {
    if (typeof this.#config.elementContainer === "object" && this.#config.elementContainer instanceof HTMLElement) {
      return this.#config.elementContainer;
    }

    if (this.#config.elementContainer?.length) {
      const element = document?.querySelector(this.#config.elementContainer);
      if (!element) {
        devConsole.warn(`DOM element not found for selector: ${this.#config.elementContainer}`);
        return;
      }
      return element as HTMLElement;
    }

    return document?.body;
  }

  /**
   * Works with both a popup or embedded element, as well as an existing one on elementContainer.
   * If a submit event is provided, it will be used to attempt a login before showing the login element.
   * @returns a promise that awaits for the submit to be successful
   */
  async showLogin(options?: HygienaLoginOptions): Promise<UnifiedLoginStateUpdate | undefined> {
    const redirectUri = options?.redirectUri ?? this.#config?.redirectUri;
    let initialErrorMessage = "";

    try {
      if (options?.loginHint && options?.password) {
        const loginResult = await super.login({
          username: options.loginHint,
          password: options.password,
        });
        if (loginResult) {
          if (redirectUri) {
            window.location.href = redirectUri;
          }

          return loginResult;
        }
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : "Login failed.";
      initialErrorMessage = message.length ? message : "";
    }
    registerHygienaAuthElement();
    // either embed the web component for popup or form
    const component = this.#getOrMountWebComponentElement();

    component.clear();
    if (options?.loginHint) {
      component.setUsername(options.loginHint);
    }
    if (initialErrorMessage.length) {
      component.setErrors([initialErrorMessage]);
    }

    component.openPopup(); // only works on popup mode
    component.focusUsername();

    return await new Promise<UnifiedLoginStateUpdate | undefined>((resolve, reject) => {
      this.#abortController = new AbortController();

      const handleReject = (event: Event) => {
        this.#cleanupAbortController();
        reject(
          new HygienaAuthError("User closed the login popup.", {
            cause: event,
          }),
        );
      };

      const handleSubmit = async (event: Event): Promise<void> => {
        try {
          const loginResult = await this.#handleLogin(event as CustomEvent<SubmitEvent>);

          if (!loginResult) {
            return;
          }

          this.#cleanupAbortController();
          await resolve(loginResult);
          component.closePopup();

          if (redirectUri) {
            window.location.href = redirectUri;
          }
        } catch (error) {
          const message = error instanceof Error ? error.message : "Login failed.";
          component.setErrors([message]);
        }
      };

      component.addEventListener(HygienaAuthElement.EVENT_TYPES.authSubmit, handleSubmit, {
        signal: this.#abortController.signal,
      });
      component.addEventListener(HygienaAuthElement.EVENT_TYPES.popupClosed, handleReject, {
        signal: this.#abortController.signal,
      });
    });
  }

  override async logout(option?: HygienaLogoutOptions): Promise<void> {
    await super.logout();

    const component = this.#getExistingComponent();
    component?.clearErrors();
    component?.clear();
    component?.closePopup();
    this.#cleanupAbortController();

    if (option?.postLogoutRedirectUri) {
      window.location.href = option.postLogoutRedirectUri;
    }
  }

  #parseSubmitEvent(submitEvent: SubmitEvent): { username: string; password: string } | undefined {
    const form = submitEvent?.target instanceof HTMLFormElement ? submitEvent.target : null;

    if (!form) {
      return;
    }

    const formData = new FormData(form);
    const username = String(formData.get("username") ?? "");
    const password = String(formData.get("password") ?? "");

    return { username, password };
  }

  #handleLogin(event: CustomEvent<SubmitEvent>) {
    const submitEvent = event.detail;
    const payload = this.#parseSubmitEvent(submitEvent);
    if (!payload) return;

    return super.login(payload);
  }

  #getExistingComponent(): HygienaAuthElementApi | null {
    if (!this.elementContainer) return null;

    if (this.elementContainer instanceof HygienaAuthElement) {
      return this.elementContainer;
    }
    return this.elementContainer.querySelector("hygiena-auth") as HygienaAuthElementApi | null;
  }

  #onPopupOpened = (e: Event) => this.#raisePopupOpenedEvent(e);
  #onPopupClosed = (e: Event) => this.#raisePopupClosedEvent(e);

  #raisePopupOpenedEvent(event: Event): void {
    const customEvent = event as CustomEvent;
    const popupOpenedEvent = new CustomEvent("auth-popup-opened", {
      detail: customEvent.detail,
      bubbles: true,
      composed: true,
    }) as HygienaEventMessage;
    this.broadcastEvent(popupOpenedEvent);
  }

  #raisePopupClosedEvent(event: Event): void {
    const customEvent = event as CustomEvent;
    const popupClosedEvent = new CustomEvent("auth-popup-closed", {
      detail: customEvent.detail,
      bubbles: true,
      composed: true,
    }) as HygienaEventMessage;

    this.broadcastEvent(popupClosedEvent);
  }

  // this should live on hygiena-auth provider or a helper
  #getOrMountWebComponentElement() {
    const existing = this.#getExistingComponent();
    const element =
      existing ??
      (this.elementContainer?.appendChild(document.createElement("hygiena-auth")) as HygienaAuthElementApi | undefined);

    if (!element) {
      throw new Error("Failed to mount Hygiena Auth component.");
    }

    if (element !== this.#boundComponent) {
      this.#unmountComponent();

      element.addEventListener(HygienaAuthElement.EVENT_TYPES.popupOpened, this.#onPopupOpened);
      element.addEventListener(HygienaAuthElement.EVENT_TYPES.popupClosed, this.#onPopupClosed);
      this.#boundComponent = element;
    }

    const attributes = this.#config.attributes ?? {};
    const labels = this.#config.labels ?? {};

    for (const [key, value] of Object.entries(attributes)) {
      if (value === undefined || value === null) {
        continue;
      }

      const attributeValue = typeof value === "function" ? value() : value.toString();
      element.setAttribute(key, attributeValue);
    }

    for (const [key, value] of Object.entries(labels)) {
      if (value === undefined || value === null) {
        continue;
      }

      const slotValue = typeof value === "function" ? value() : value;
      const existingSlot = element.querySelector(`[slot="${key}"]`);
      if (!slotValue) {
        existingSlot?.remove();
        continue;
      }
      const slot = existingSlot ?? element.appendChild(document.createElement("span"));
      slot.textContent = slotValue;
      slot.setAttribute("slot", key);
    }

    return element;
  }

  #cleanupAbortController() {
    if (this.#abortController) {
      this.#abortController.abort();
      this.#abortController = undefined;
    }
  }

  #unmountComponent() {
    if (!this.#boundComponent) return;
    try {
      this.#cleanupAbortController();
      this.#boundComponent.removeEventListener(HygienaAuthElement.EVENT_TYPES.popupOpened, this.#onPopupOpened);
      this.#boundComponent.removeEventListener(HygienaAuthElement.EVENT_TYPES.popupClosed, this.#onPopupClosed);
      this.#boundComponent.remove();
      this.#boundComponent = undefined;
    } catch (e) {
      devConsole.error("Failed to unmount Hygiena Auth component:", e);
    }
  }
}
